import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Users, CalendarDays, Bell, Settings } from 'lucide-react';

export type ResponsavelTabKey =
  | 'home'
  | 'filhos'
  | 'freq'
  | 'avisos'
  | 'perfil'
  | 'justif'
  | 'escola'
  | 'desempenho'
  | 'obs';

interface Props {
  activeTab?: ResponsavelTabKey;
  badgeAvisos?: number;
}

export const ResponsavelTabBar: React.FC<Props> = ({ activeTab = 'home', badgeAvisos }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState<number>(badgeAvisos || 0);

  useEffect(() => {
    if (typeof badgeAvisos === 'number') {
      setUnreadCount(badgeAvisos);
    }
  }, [badgeAvisos]);

  // Escuta eventos de leitura e sincronização de comunicados em tempo real
  useEffect(() => {
    const handleReadSync = () => {
      try {
        const unreadCache = localStorage.getItem('ac_unread_comunicados_count');
        if (unreadCache !== null) {
          const val = parseInt(unreadCache, 10);
          if (!isNaN(val)) setUnreadCount(val);
        }
      } catch {}
    };

    window.addEventListener('ac:comunicado:read', handleReadSync);
    window.addEventListener('ac:comunicados:sync', handleReadSync);
    return () => {
      window.removeEventListener('ac:comunicado:read', handleReadSync);
      window.removeEventListener('ac:comunicados:sync', handleReadSync);
    };
  }, []);

  const tabs = [
    { key: 'home' as const, icon: Home, label: 'Início', to: '/responsavel/dashboard' },
    { key: 'filhos' as const, icon: Users, label: 'Filhos', to: '/responsavel/filhos' },
    { key: 'freq' as const, icon: CalendarDays, label: 'Frequência', to: '/responsavel/frequencia' },
    { key: 'avisos' as const, icon: Bell, label: 'Avisos', to: '/responsavel/comunicados', badge: unreadCount },
    { key: 'perfil' as const, icon: Settings, label: 'Perfil', to: '/responsavel/perfil' },
  ];

  // Mapeia sub-páginas para a aba principal correspondente caso aplicável
  const normalizedActive = (() => {
    if (activeTab === 'justif' || activeTab === 'escola' || activeTab === 'desempenho' || activeTab === 'obs') {
      return 'home';
    }
    return activeTab;
  })();

  return (
    <div className="resp-tab-bar" role="navigation" aria-label="Menu Principal do Responsável">
      {tabs.map((tab) => {
        const isActive = normalizedActive === tab.key;
        const Icon = tab.icon;
        return (
          <button
            key={tab.key}
            type="button"
            className={`resp-tab-item ${isActive ? 'active' : ''}`}
            onClick={() => navigate(tab.to)}
            aria-label={tab.label}
          >
            <div style={{ position: 'relative' }}>
              <Icon size={20} />
              {!!tab.badge && tab.badge > 0 && (
                <span className="resp-tab-notif">{tab.badge > 9 ? '9+' : tab.badge}</span>
              )}
            </div>
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
};
